import React, { useEffect, useRef, useState } from "react";
import api from "../setupAxios";
import TopBar from "../components/TopBar";
import Badge from "../components/Badge";
import { useAuth } from "../context/authContext";
import { avisarCambioResumen, onActualizarPanel, onCambioResumen } from "../statsBus";
import { isoToEs } from "../fechas";
import { tonoEstado, etiquetaEstado } from "../estados";
import { useDialog } from "../context/dialogContext";
import { pedirConfirmacionRevision } from "../revision";
import { useFiltroColumnas, FilterHead, PanelHead } from "../components/TableFilter";
import FichaLlaveModal from "../components/FichaLlave";
import { usePaginacion, Paginador } from "../components/Paginacion";
import PisoAutocomplete, { textoPiso } from "../components/PisoAutocomplete";

// Fecha de hoy en ISO (huso del navegador) — la devolución estimada nunca
// puede ser anterior a hoy.
const hoyIso = () => new Date().toISOString().slice(0, 10);

// Texto + fila de una línea de ruta según su estado, MISMO criterio en toda
// la vista de Rutas: no disponible -> rojo, preparada -> verde, pendiente ->
// sin tinte (neutro, aún por hacer).
function estadoLinea(l) {
  if (l.no_disponible) return "No disponible";
  if (l.por_entregar) return "Por entregar";
  if (l.preparada) return "Preparada";
  return "Pendiente";
}
function claseFilaLinea(l) {
  if (l.no_disponible) return "fila-error";
  if (l.preparada) return "fila-ok";
  return "";
}

// Hoy en España (YYYY-MM-DD), independientemente del huso del navegador:
// la descarga masiva filtra por las fechas de la ruta, que se guardan en
// hora de España.
const hoyEs = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" });

// Descarga un PDF del backend (con el JWT de axios) y lo guarda con el
// nombre que manda el servidor en Content-Disposition.
async function descargarPdf(url, params, nombrePorDefecto) {
  try {
    const res = await api.get(url, { params, responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = cd.match(/filename="?([^";]+)"?/);
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = m ? m[1] : nombrePorDefecto;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
    return null;
  } catch (err) {
    // Con responseType "blob" el JSON de error también llega como Blob.
    try {
      const txt = await err.response?.data?.text?.();
      return JSON.parse(txt).error || "No se pudo descargar.";
    } catch {
      return "No se pudo descargar.";
    }
  }
}

// Suscribe `cargar` al botón "Actualizar" global de la barra superior.
function useActualizarGlobal(cargar) {
  useEffect(() => onActualizarPanel(cargar), [cargar]);
}

// Filtro de estado como grupo de botones (mismo estilo que Administración).
function FiltroEstado({ value, onChange, opciones }) {
  return (
    <div className="seg-buttons" style={{ marginBottom: 12 }}>
      {opciones.map(([v, label]) => (
        <button key={v} className={value === v ? "on" : ""} data-tone={tonoEstado(v)} onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

// Qué columnas de fecha mostrar en un listado de rutas según el estado que
// se está viendo: cuanto más avanzada la solicitud, más fechas hay que
// enseñar (pedida -> prestada -> entregada -> devuelta). 'pedido' y
// 'en_preparacion' solo tienen la primera fecha; a partir de 'entregado' (o
// al ver 'todas', donde puede haber de todo) se enseñan las 4.
function columnasFecha(estado) {
  const TODAS = [
    ["fecha_pedido", "Fecha pedido"],
    ["fecha_preparado", "Fecha prestada"],
    ["fecha_entrega", "Fecha entregado"],
    ["fecha_devuelto", "Fecha devuelto"],
  ];
  if (estado === "preparado") return TODAS.slice(0, 2);
  if (estado === "entregado" || estado === "todas") return TODAS;
  return TODAS.slice(0, 1);
}

/*
  Rutas.jsx — Solicitudes de recogida de llaves.
   - Rol 'personal' (equipo, incluido el tipo 'llaves' con acceso limitado):
     pide llaves ("Nueva solicitud"), ve lo que ha pedido ("Mis solicitudes")
     y lo que ha llegado a tener ("Mis llaves").
   - Rol 'llaves' (responsable/admin): NO pide nada aquí, solo gestiona lo que
     pide el resto, con una pestaña por estado: "Pendientes de preparar"
     (pedido), "A entregar" (preparado) y "Entregadas" (entregado) — entre
     las tres se ve el 100% de las rutas, porque esos son los 3 únicos
     estados posibles.

  Adaptado al backend nuevo: cada línea de ruta se identifica por `linea_id`;
  la fecha de la ruta es `fecha_pedido`; el estado de la ruta recorre
  pedido -> preparado -> entregado. La persona del préstamo es el propio
  solicitante (no se elige aquí), así que el formulario de creación sólo pide
  pisos, tipo y devolución estimada.
*/
export default function Rutas() {
  const { user } = useAuth();
  const esLlaves = user?.tipo === "llaves";
  const [tab, setTab] = useState(esLlaves ? "pendientes" : "crear");
  const [nPendientes, setNPendientes] = useState(0);
  const [nAEntregar, setNAEntregar] = useState(0);
  const [descargaMasiva, setDescargaMasiva] = useState(false);

  // Contadores de las pestañas: se cargan aquí (no solo desde la pestaña
  // abierta), para que estén bien en todas al entrar, al pulsar "Actualizar"
  // y cuando cualquier acción cambia algo.
  useEffect(() => {
    if (!esLlaves) return undefined;
    const cargar = () => {
      api.get("/rutas/pendientes").then(({ data }) => setNPendientes(data.n_rutas || 0)).catch(() => {});
      api.get("/rutas/preparadas", { params: { estado: "preparado" } })
        .then(({ data }) => setNAEntregar((data.rows || []).length)).catch(() => {});
    };
    cargar();
    const quitarCambio = onCambioResumen(cargar);
    const quitarActualizar = onActualizarPanel(cargar);
    return () => { quitarCambio(); quitarActualizar(); };
  }, [esLlaves]);

  return (
    <div className="app-shell">
      <TopBar />
      <div className="subnav">
        {esLlaves ? (
          <>
            <button className={`${tab === "pendientes" ? "active" : ""} ${nPendientes > 0 ? "alerta" : ""}`} onClick={() => setTab("pendientes")}>
              ⏳ Pendientes de preparar
              {nPendientes > 0 && <span className="tab-badge">{nPendientes}</span>}
            </button>
            <button className={tab === "aentregar" ? "active" : ""} onClick={() => setTab("aentregar")}>
              📦 A entregar
              {nAEntregar > 0 && <span className="tab-badge tab-badge-neutral">{nAEntregar}</span>}
            </button>
            <button className={tab === "entregadas" ? "active" : ""} onClick={() => setTab("entregadas")}>✅ Entregadas</button>
            <button style={{ marginLeft: "auto" }} className="btn" onClick={() => setDescargaMasiva(true)}>⬇️ Descarga masiva</button>
          </>
        ) : (
          <>
            <button className={tab === "crear" ? "active" : ""} onClick={() => setTab("crear")}>📝 Nueva solicitud</button>
            <button className={tab === "mias" ? "active" : ""} onClick={() => setTab("mias")}>📋 Mis solicitudes</button>
            <button className={tab === "misllaves" ? "active" : ""} onClick={() => setTab("misllaves")}>🔑 Mis llaves</button>
          </>
        )}
      </div>
      {!esLlaves && tab === "crear" && <CrearSolicitud />}
      {!esLlaves && tab === "mias" && <MisSolicitudes />}
      {!esLlaves && tab === "misllaves" && <MisLlaves />}
      {esLlaves && tab === "pendientes" && <Pendientes onCount={setNPendientes} />}
      {esLlaves && tab === "aentregar" && <AEntregar onCount={setNAEntregar} />}
      {esLlaves && tab === "entregadas" && <Entregadas />}
      {esLlaves && descargaMasiva && <DescargaMasivaModal onClose={() => setDescargaMasiva(false)} />}
    </div>
  );
}

// ------------------------------------------------------------- CREAR SOLICITUD
function CrearSolicitud() {
  // Cada línea se valida sola al pulsar Intro (o al salir del campo), y se
  // pinta en verde/rojo al momento — no hace falta un botón "Previsualizar"
  // aparte. { texto, estado: null antes de validar | fila de /previsualizar }
  const [lineas, setLineas] = useState([{ texto: "", estado: null }]);
  const [devol, setDevol] = useState("");
  const [tipoPrestamo, setTipoPrestamo] = useState("provisional");
  const [msg, setMsg] = useState(null);
  const [creando, setCreando] = useState(false);
  const refs = useRef([]);

  const validarLinea = async (i, textoLinea) => {
    if (!textoLinea.trim()) return;
    try {
      const { data } = await api.post("/rutas/previsualizar", { texto: textoLinea });
      const fila = (data.filas || [])[0] || null;
      setLineas((prev) => {
        if (!prev[i] || prev[i].texto !== textoLinea) return prev; // se editó mientras tanto
        const copia = [...prev];
        copia[i] = { texto: textoLinea, estado: fila };
        return copia;
      });
    } catch {
      // sin conexión: deja la línea sin validar, se revalidará al crear
    }
  };

  const onChangeLinea = (i, valor) => {
    setLineas((prev) => {
      const copia = [...prev];
      copia[i] = { texto: valor, estado: null }; // al tocarla, se invalida el color hasta revalidar
      return copia;
    });
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const textoLinea = e.target.value;
      validarLinea(i, textoLinea);
      setLineas((prev) => (i === prev.length - 1 ? [...prev, { texto: "", estado: null }] : prev));
      setTimeout(() => refs.current[i + 1]?.focus(), 0);
    } else if (e.key === "Backspace" && e.target.value === "" && lineas.length > 1) {
      e.preventDefault();
      setLineas((prev) => prev.filter((_, idx) => idx !== i));
      setTimeout(() => refs.current[Math.max(0, i - 1)]?.focus(), 0);
    }
  };

  // Piso elegido del desplegable: la línea queda "id abreviatura", se valida
  // y se pasa a la siguiente (como al pulsar Intro).
  const elegirPisoLinea = (i, p) => {
    const t = textoPiso(p);
    setLineas((prev) => {
      const copia = [...prev];
      copia[i] = { texto: t, estado: null };
      return i === copia.length - 1 ? [...copia, { texto: "", estado: null }] : copia;
    });
    validarLinea(i, t);
    setTimeout(() => refs.current[i + 1]?.focus(), 0);
  };

  const quitarLinea = (i) => {
    setLineas((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
  };

  // Si se pega un texto con varias líneas (p.ej. copiado de un Excel/Sheet),
  // se reparte una línea por cada renglón en vez de meterlo todo junto en el
  // campo actual.
  const onPasteLinea = (i, e) => {
    const texto = e.clipboardData.getData("text");
    if (!texto.includes("\n")) return; // pegado de una sola línea: comportamiento normal
    e.preventDefault();
    const partes = texto.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!partes.length) return;
    setLineas((prev) => {
      const copia = [...prev];
      copia.splice(i, 1, ...partes.map((t) => ({ texto: t, estado: null })));
      if ((copia[copia.length - 1]?.texto || "").trim()) copia.push({ texto: "", estado: null });
      return copia;
    });
    partes.forEach((t, idx) => validarLinea(i + idx, t));
    setTimeout(() => refs.current[i + partes.length]?.focus(), 0);
  };

  const crear = async () => {
    setMsg(null);
    const conTexto = lineas.filter((l) => l.texto.trim());
    if (!conTexto.length) {
      setMsg({ tipo: "error", texto: "Escribe al menos un piso." });
      return;
    }
    if (!devol) {
      setMsg({ tipo: "error", texto: "La devolución estimada es obligatoria." });
      return;
    }
    if (devol < hoyIso()) {
      setMsg({ tipo: "error", texto: "La devolución estimada no puede ser anterior a hoy." });
      return;
    }
    setCreando(true);
    // Revalida todo junto justo antes de crear, por si alguna línea se dejó
    // sin pulsar Intro (p.ej. la última antes de dar a "Crear solicitud").
    let filas;
    try {
      const { data } = await api.post("/rutas/previsualizar", { texto: conTexto.map((l) => l.texto).join("\n") });
      filas = data.filas || [];
    } catch {
      setMsg({ tipo: "error", texto: "No se pudo comprobar los pisos." });
      setCreando(false);
      return;
    }
    setLineas([...filas.map((f) => ({ texto: `${f.id_origen_txt} ${f.codigo_piso || ""}`.trim(), estado: f })),
               { texto: "", estado: null }]);
    const noEncontrados = filas.filter((f) => !f.existe);
    if (noEncontrados.length) {
      const lista = noEncontrados.map((f) => f.id_origen_txt || "(vacío)").join(", ");
      setMsg({ tipo: "error", texto: `No se encuentran estos pisos: ${lista}. Corrígelos (mira las líneas en rojo).` });
      setCreando(false);
      return;
    }
    const lineasPost = filas.map((f) => ({ id_origen_txt: f.id_origen_txt, codigo_piso: f.codigo_piso, fecha_devol: devol, tipo_prestamo: tipoPrestamo }));
    try {
      const { data } = await api.post("/rutas/crear", { lineas: lineasPost });
      if (data.ok) {
        setMsg({ tipo: "ok", texto: `Solicitud creada (nº ${data.ruta_id}) · ${data.creadas} línea(s).` });
        setLineas([{ texto: "", estado: null }]); setDevol(""); setTipoPrestamo("provisional");
        avisarCambioResumen();
      } else {
        setMsg({ tipo: "error", texto: data.error || "No se pudo crear." });
      }
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "Error al crear la solicitud." });
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="card">
      <h3>Nueva solicitud de ruta</h3>
      {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}
      <label>Pisos (nº de piso y opcionalmente el código — pulsa Intro para pasar a la siguiente línea)</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {lineas.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <PisoAutocomplete
              inputRef={(el) => { refs.current[i] = el; }}
              className={`linea-piso ${l.estado ? (l.estado.existe ? "linea-ok" : "linea-error") : ""}`}
              wrapStyle={{ flex: 1 }}
              style={{ width: "100%" }}
              value={l.texto}
              placeholder={i === 0 ? "ID o abreviatura: 29 / AB1.1B(1)" : i === 1 ? "42" : "264    SJB20.2D (15)"}
              onChange={(v) => onChangeLinea(i, v)}
              onSelect={(p) => elegirPisoLinea(i, p)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onPaste={(e) => onPasteLinea(i, e)}
              onBlur={(e) => validarLinea(i, e.target.value)}
              autoFocus={i === 0}
            />
            {l.estado && (
              <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                {l.estado.existe ? `✓ ${l.estado.codigo_real || ""}` : "✗ no encontrado"}
              </span>
            )}
            {lineas.length > 1 && (
              <button type="button" className="btn btn-mini" onClick={() => quitarLinea(i)}>✕</button>
            )}
          </div>
        ))}
      </div>
      <div className="row">
        <div>
          <label>Devolución estimada *</label>
          <input type="date" value={devol} min={hoyIso()} onChange={(e) => setDevol(e.target.value)} required />
        </div>
        <div>
          <label>Tipo</label>
          <div className="seg-buttons">
            <button type="button" className={tipoPrestamo === "provisional" ? "on" : ""} onClick={() => setTipoPrestamo("provisional")}>Provisional</button>
            <button type="button" className={tipoPrestamo === "fija" ? "on" : ""} onClick={() => setTipoPrestamo("fija")}>Fija</button>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button className="btn btn-primary" onClick={crear} disabled={creando}>
          {creando ? "Creando…" : "Crear solicitud"}
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- MIS SOLICITUDES
function MisSolicitudes() {
  const [rows, setRows] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("todas");
  const [detalle, setDetalle] = useState(null);
  const [infoLinea, setInfoLinea] = useState(null);
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState("");
  const [devol, setDevol] = useState("");
  const [previa, setPrevia] = useState([]);
  const [msg, setMsg] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = () => api.get("/rutas/mias").then(({ data }) => setRows(data.rows || []));
  useEffect(() => { cargar(); }, []);
  useActualizarGlobal(cargar);

  const preparadas = rows.filter((r) => r.estado === "preparado").length;
  const filasFiltradas = filtroEstado === "todas" ? rows : rows.filter((r) => r.estado === filtroEstado);

  const ver = async (id) => {
    setEditando(false); setMsg(null);
    const { data } = await api.get(`/rutas/mia_detalle/${id}`);
    if (data.ok) setDetalle({ ruta: data.ruta, lineas: data.lineas });
  };

  const empezarEdicion = () => {
    const lineas = [...detalle.lineas].filter((l) => !l.no_disponible);
    setTexto(lineas.map((l) => `${l.pid} ${l.piso_codigo || ""}`.trim()).join("\n"));
    setDevol((lineas[0]?.fecha_devol ? String(lineas[0].fecha_devol).slice(0, 10) : ""));
    setPrevia([]); setMsg(null);
    setEditando(true);
  };

  const previsualizar = async (t) => {
    const { data } = await api.post("/rutas/previsualizar", { texto: t ?? texto });
    const filas = data.filas || [];
    setPrevia(filas);
    return filas;
  };

  const guardar = async () => {
    setMsg(null);
    if (!texto.trim()) { setMsg({ tipo: "error", texto: "Escribe al menos un piso." }); return; }
    if (!devol) { setMsg({ tipo: "error", texto: "La devolución estimada es obligatoria." }); return; }
    if (devol < hoyIso()) { setMsg({ tipo: "error", texto: "La devolución estimada no puede ser anterior a hoy." }); return; }
    setGuardando(true);
    let filas;
    try {
      filas = await previsualizar();
    } catch {
      setMsg({ tipo: "error", texto: "No se pudo comprobar los pisos." });
      setGuardando(false);
      return;
    }
    const noEncontrados = filas.filter((f) => !f.existe);
    if (noEncontrados.length) {
      const lista = noEncontrados.map((f) => f.id_origen_txt || "(vacío)").join(", ");
      setMsg({ tipo: "error", texto: `No se encuentran estos pisos: ${lista}. Corrígelos (mira las líneas en rojo abajo).` });
      setGuardando(false);
      return;
    }
    const lineas = filas.map((f) => ({ id_origen_txt: f.id_origen_txt, codigo_piso: f.codigo_piso, fecha_devol: devol }));
    try {
      const { data } = await api.post("/rutas/editar", { ruta_id: detalle.ruta.id, lineas });
      if (data.ok) {
        setEditando(false);
        cargar();
        await ver(detalle.ruta.id);
      } else {
        setMsg({ tipo: "error", texto: data.error || "No se pudo guardar." });
      }
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "Error al guardar los cambios." });
    } finally {
      setGuardando(false);
    }
  };

  // Todo llega del servidor (sin tope); se pinta de 100 en 100.
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(filasFiltradas, [rows, filtroEstado]);
  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>Mis solicitudes</h3>
        {preparadas > 0 && <span className="tab-badge">🔔 {preparadas} preparada(s) para recoger</span>}
      </div>
      <FiltroEstado value={filtroEstado} onChange={setFiltroEstado} opciones={[
        ["todas", "Todas"], ["pedido", etiquetaEstado("pedido")], ["en_preparacion", etiquetaEstado("en_preparacion")],
        ["preparado", etiquetaEstado("preparado")], ["entregado", etiquetaEstado("entregado")],
      ]} />
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Nº</th>
              {columnasFecha(filtroEstado).map(([k, label]) => <th key={k}>{label}</th>)}
              <th>Estado</th><th>Llaves</th><th></th>
            </tr>
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                {columnasFecha(filtroEstado).map(([k]) => <td key={k}>{isoToEs(r[k]) || "—"}</td>)}
                <td><Badge tone={tonoEstado(r.estado)}>{etiquetaEstado(r.estado)}</Badge></td>
                <td>{r.n_listas || 0}/{r.n_llaves || 0}</td>
                <td><button className="btn" onClick={() => ver(r.id)}>Ver</button></td>
              </tr>
            ))}
            {filasFiltradas.length === 0 && (
              <tr><td colSpan={columnasFecha(filtroEstado).length + 4} className="empty">Sin resultados.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />
      {detalle && !editando && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h4 style={{ margin: 0 }}>Solicitud nº {detalle.ruta.id}</h4>
            {detalle.ruta.editable && (
              <button className="btn btn-mini" onClick={empezarEdicion}>✏️ Editar</button>
            )}
          </div>
          <div className="tablewrap" style={{ marginTop: 10 }}>
            <table>
              <thead><tr><th>Piso</th><th>ID piso</th><th>Código llave</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {detalle.lineas.map((l, i) => (
                  <tr key={i} className={claseFilaLinea(l)}>
                    <td>{l.piso_codigo || "—"}</td>
                    <td className="mono">{l.pid ?? "—"}</td>
                    <td className="mono">{l.codigo_llave ?? "—"}</td>
                    <td><Badge tone={tonoEstado(estadoLinea(l))}>{estadoLinea(l)}</Badge></td>
                    <td><button className="btn btn-mini" onClick={() => setInfoLinea(l)}>Info</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {infoLinea && (
        <div className="modal-back" onMouseDown={() => setInfoLinea(null)}>
          <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
            <h3>🔑 {infoLinea.piso_codigo || "—"}</h3>
            <p><b>ID piso:</b> {infoLinea.pid ?? "—"}</p>
            <p><b>Cajetín:</b> {infoLinea.cajetin ?? "—"}</p>
            <p><b>Código llave:</b> {infoLinea.codigo_llave ?? "—"}</p>
            <p><b>Estado:</b> <Badge tone={tonoEstado(estadoLinea(infoLinea))}>{estadoLinea(infoLinea)}</Badge></p>
            <p><b>Devolución estimada:</b> {isoToEs(infoLinea.fecha_devol) || "—"}</p>
            <div className="modal-actions">
              <button className="btn" onClick={() => setInfoLinea(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      {detalle && editando && (
        <div style={{ marginTop: 16 }}>
          <h4>Editando solicitud nº {detalle.ruta.id}</h4>
          {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}
          <label>Pisos (uno por línea: nº de piso y opcionalmente el código)</label>
          <textarea rows={6} value={texto} onChange={(e) => setTexto(e.target.value)} />
          <div className="row">
            <div>
              <label>Devolución estimada *</label>
              <input type="date" value={devol} min={hoyIso()} onChange={(e) => setDevol(e.target.value)} required />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <button className="btn" onClick={() => previsualizar()}>Previsualizar</button>
            <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
              {guardando ? "Guardando…" : "Guardar cambios"}
            </button>
            <button className="btn" onClick={() => setEditando(false)}>Cancelar</button>
          </div>
          {previa.length > 0 && (
            <div className="tablewrap" style={{ marginTop: 16 }}>
              <table>
                <thead><tr><th>Nº</th><th>Código</th><th>Estado</th></tr></thead>
                <tbody>
                  {previa.map((f, i) => (
                    <tr key={i} className={f.existe ? "fila-ok" : "fila-error"}>
                      <td>{f.id_origen_txt || "—"}</td>
                      <td>{f.codigo_real || f.codigo_piso || "—"}</td>
                      <td>{f.existe ? "✓ encontrado" : "✗ no encontrado"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- MIS LLAVES
function MisLlaves() {
  const [rows, setRows] = useState([]);
  const [estado, setEstado] = useState("prestada"); // 'prestada' | 'todas'
  const [info, setInfo] = useState(null);

  const cargar = () => api.get("/rutas/mis_llaves", { params: { estado } })
    .then(({ data }) => setRows(data.rows || []));
  useEffect(() => { cargar(); }, [estado]); // eslint-disable-line react-hooks/exhaustive-deps
  useActualizarGlobal(cargar);

  const fmt = (v) => isoToEs(v) || "—";

  // Todo llega del servidor (sin tope); se pinta de 100 en 100.
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(rows, [rows]);
  return (
    <div className="card">
      <h3>Mis llaves</h3>
      <FiltroEstado value={estado} onChange={setEstado} opciones={[
        ["prestada", "Solo prestadas"], ["todas", "Todas"],
      ]} />
      <div className="tablewrap">
        <table>
          <thead><tr><th>Piso</th><th>ID piso</th><th>Cajetín</th><th>Código llave</th><th>Estado</th><th>Ruta</th><th></th></tr></thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.mov_id}>
                <td>{r.piso}</td>
                <td className="mono">{r.pid ?? "—"}</td>
                <td>{r.cajetin ?? "—"}</td>
                <td className="mono">{r.llave ?? "—"}</td>
                <td><Badge tone={tonoEstado(r.estado)}>{r.estado}</Badge></td>
                <td className="mono">{r.ruta_id ? `#${r.ruta_id}` : "—"}</td>
                <td><button className="btn btn-mini" onClick={() => setInfo(r)}>Info</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="empty">Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />
      {info && (
        <div className="modal-back" onMouseDown={() => setInfo(null)}>
          <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
            <h3>🔑 {info.piso}</h3>
            <p><b>ID piso:</b> {info.pid ?? "—"}</p>
            <p><b>Cajetín:</b> {info.cajetin ?? "—"}</p>
            <p><b>Código llave:</b> {info.llave ?? "—"}</p>
            <p><b>Estado:</b> <Badge tone={tonoEstado(info.estado)}>{info.estado}</Badge></p>
            <p><b>De la ruta:</b> {info.ruta_id ? `#${info.ruta_id}` : "—"}</p>
            <p><b>Salida:</b> {fmt(info.salida)}</p>
            <p><b>Devolución estimada:</b> {fmt(info.devol)}</p>
            <p><b>Devuelta:</b> {fmt(info.entrada)}</p>
            {info.ruta_id && (
              <>
                <p><b>Fecha pedida:</b> {fmt(info.ruta_pedida)}</p>
                <p><b>Fecha preparada:</b> {fmt(info.ruta_preparada)}</p>
                <p><b>Fecha entrega:</b> {fmt(info.ruta_entrega)}</p>
              </>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={() => setInfo(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- PENDIENTES (llaves)
function Pendientes({ onCount }) {
  const [rows, setRows] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [cod, setCod] = useState({});
  // Líneas con el botón "Preparar" activado: al pulsar OK la llave queda en
  // 'preparado' (apartada, sin entregar) en vez de pasar directamente a
  // 'prestada'. Desactivado por defecto.
  const [soloPreparar, setSoloPreparar] = useState({});
  const [ficha, setFicha] = useState(null); // {linea, mensaje} cuando hay que confirmar+rellenar una llave nueva
  const [editar, setEditar] = useState(null); // ruta (fila) que se está editando
  const { alertaRevision, avisar, confirmarPeligro, exito } = useDialog();

  const eliminar = async (r) => {
    const preparadas = r.n_listas || 0;
    const ok = await confirmarPeligro(
      `Vas a ELIMINAR la solicitud nº ${r.id} de ${r.solicitante} con ${r.n_llaves || 0} piso(s).` +
      (preparadas ? `\n\n${preparadas} llave(s) ya preparada(s) volverán a quedar disponibles.` : "") +
      "\n\nEsta acción no se puede deshacer.",
      { titulo: "Eliminar solicitud", boton: "Sí, eliminar" },
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/rutas/eliminar/${r.id}`);
      if (data.ok) {
        if (detalle?.ruta.id === r.id) setDetalle(null);
        cargar(); avisarCambioResumen();
      }
    } catch (err) {
      avisar(err.response?.data?.error || "No se pudo eliminar la solicitud.");
    }
  };

  const cargar = () => api.get("/rutas/pendientes").then(({ data }) => {
    setRows(data.rows || []);
    onCount?.(data.n_rutas || 0);
  });
  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useActualizarGlobal(cargar);

  const campos = [
    ["id", "Nº", (r) => r.id],
    ["solicitante", "Solicitante", (r) => r.solicitante],
    ["fecha", "Fecha pedido", (r) => isoToEs(r.fecha_pedido)],
    ["estado", "Estado", (r) => etiquetaEstado(r.estado)],
    ["progreso", "Progreso", (r) => `${r.n_listas || 0}/${r.n_llaves || 0}`],
  ];
  const { filtros, setFiltro, limpiar, filtradas } = useFiltroColumnas(rows, campos);

  const abrir = async (id) => {
    // Se marca 'en_preparacion' en el mismo instante en que se pulsa
    // "Preparar" (antes de tocar ninguna línea): a partir de aquí el
    // solicitante ya no puede editarla. Es idempotente — si ya estaba en
    // preparación (p.ej. al refrescar tras preparar una línea), no hace nada.
    await api.post(`/rutas/empezar_preparar/${id}`);
    const { data } = await api.get(`/rutas/detalle/${id}`);
    if (data.ok) setDetalle({ ruta: data.ruta, lineas: data.lineas });
    cargar();
  };

  // Si el solicitante ha editado la solicitud justo antes de que se
  // preparase/descartase la primera línea (mientras seguía en 'pedido'), las
  // líneas viejas ya no existen: el backend responde 409 + obsoleta:true. En
  // vez de dejar el alert suelto, se recarga sola para que se vea al momento
  // lo que ha quedado tras la edición.
  const avisoObsoleta = async (err, msgGenerico) => {
    if (err.response?.data?.obsoleta) {
      await avisar(err.response.data.error);
      await abrir(detalle.ruta.id);
    } else {
      avisar(err.response?.data?.error || msgGenerico);
    }
  };

  // Líneas cuyo aviso de revisión 🚩 ya se ha confirmado (linea_id ->
  // {codigo, desmarcar_llave, desmarcar_piso}), para no volver a preguntarlo
  // al rellenar después la ficha de llave nueva.
  const revisionOk = useRef({});
  const preparar = async (linea, camposFicha = null) => {
    const codigo = cod[linea.linea_id] || "";
    try {
      const { data } = await api.post("/rutas/preparar_llave", {
        linea_id: linea.linea_id, codigo_llave: codigo, solo_preparar: !!soloPreparar[linea.linea_id],
        ...(revisionOk.current[linea.linea_id]?.codigo === codigo
          ? { confirmado_revision: true, desmarcar_llave: revisionOk.current[linea.linea_id].desmarcar_llave,
              desmarcar_piso: revisionOk.current[linea.linea_id].desmarcar_piso }
          : {}),
        ...(camposFicha || {}),
      });
      if (data.ok) {
        delete revisionOk.current[linea.linea_id];
        setFicha(null); abrir(detalle.ruta.id); avisarCambioResumen();
      }
      else avisar(data.error || "No se pudo preparar.");
    } catch (err) {
      const d = err.response?.data;
      if (d?.requiere_revision) {
        // Marcada 🚩: confirmar (o desmarcar) antes de preparar.
        const r = await pedirConfirmacionRevision(alertaRevision, d.revision,
          { accion: "preparar", etiquetaLlave: `${linea.pid}-${codigo}`, etiquetaPiso: linea.piso_codigo || linea.pid });
        if (r.seguir) {
          revisionOk.current[linea.linea_id] = { codigo, desmarcar_llave: r.desmarcar_llave, desmarcar_piso: r.desmarcar_piso };
          preparar(linea, camposFicha);
        }
        else setFicha(null);
        return;
      }
      if (d?.requiere_ficha) {
        // Código nuevo para este piso: hay hueco libre, pero hay que
        // confirmar y rellenar la ficha (como en Crear juego) antes de
        // sustituirlo — nunca se aprende en silencio. Se precarga con la
        // configuración habitual del piso si la tiene.
        let defaults = null;
        try {
          const { data: dd } = await api.get(`/piso_defaults/${linea.id_piso}`);
          defaults = { sugerencia: dd.sugerencia, variantes: dd.variantes };
        } catch { /* sin defaults, la ficha arranca en blanco */ }
        // juego_nuevo=true (no hay ningún hueco, sube el total del piso) es
        // el caso peligroso; juego_nuevo=false es solo aprender un código en
        // un hueco ya contado, no cambia el total.
        setFicha({ linea, mensaje: d.error, defaults, peligroso: !!d.juego_nuevo });
      } else {
        await avisoObsoleta(err, "No se pudo preparar.");
      }
    }
  };

  const noDisp = async (linea) => {
    try {
      const { data } = await api.post("/rutas/no_disponible", { linea_id: linea.linea_id });
      if (data.ok) { abrir(detalle.ruta.id); avisarCambioResumen(); }
    } catch (err) {
      await avisoObsoleta(err, "No se pudo marcar como no disponible.");
    }
  };

  // Línea en 'preparado' -> 'prestada' (la llave se entrega ahora).
  const entregarLinea = async (linea) => {
    try {
      const { data } = await api.post("/rutas/entregar_linea", { linea_id: linea.linea_id });
      if (data.ok) {
        abrir(detalle.ruta.id); avisarCambioResumen();
        exito(`Llave ${linea.pid}-${linea.codigo_llave ?? "?"} (${linea.piso_codigo || "—"}) entregada: ya consta como prestada.`);
      }
    } catch (err) {
      await avisoObsoleta(err, "No se pudo entregar.");
    }
  };

  const entregar = async (rutaId) => {
    try {
      const { data } = await api.post(`/rutas/entregar/${rutaId}`);
      if (data.ok) {
        setDetalle(null); cargar(); avisarCambioResumen();
        exito(`Ruta nº ${rutaId} entregada correctamente: todas sus llaves constan como prestadas.`);
      }
    } catch (err) {
      avisar(err.response?.data?.error || "No se pudo marcar como entregada.");
    }
  };

  return (
    <div className="card">
      <PanelHead title="Solicitudes pendientes de preparar" count={filtradas.length} total={rows.length} onLimpiar={limpiar} />
      <div className="tablewrap">
        <table>
          <thead>
            <FilterHead campos={campos} filtros={filtros} setFiltro={setFiltro} extra={<th></th>} />
          </thead>
          <tbody>
            {filtradas.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{r.solicitante}</td>
                <td>{isoToEs(r.fecha_pedido) || "—"}</td>
                <td><Badge tone={tonoEstado(r.estado)}>{etiquetaEstado(r.estado)}</Badge></td>
                <td>{r.n_listas || 0}/{r.n_llaves || 0}</td>
                <td>
                  <span style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-primary" onClick={() => abrir(r.id)}>Preparar</button>
                    <button className="btn" onClick={() => setEditar(r)} title="Editar solicitud">✏️ Editar</button>
                    <button className="btn btn-danger" onClick={() => eliminar(r)} title="Eliminar solicitud">🗑️</button>
                  </span>
                </td>
              </tr>
            ))}
            {filtradas.length === 0 && <tr><td colSpan={6} className="empty">Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
      {detalle && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h4 style={{ margin: 0 }}>Preparando solicitud nº {detalle.ruta.id}</h4>
            <button className="btn btn-mini" onClick={() => setDetalle(null)} title="Cerrar">✕</button>
          </div>
          <div className="tablewrap" style={{ marginTop: 10 }}>
          <table>
            <thead><tr><th>Piso</th><th>Cajetín</th><th>ID piso</th><th>Código llave</th><th></th></tr></thead>
            <tbody>
              {detalle.lineas.map((l) => (
                <tr key={l.linea_id} className={claseFilaLinea(l)}>
                  <td>{l.piso_codigo || "—"}</td>
                  <td>{l.cajetin ?? "—"}</td>
                  <td className="mono">{l.pid ?? "—"}</td>
                  <td>
                    {l.preparada ? (
                      <Badge tone={l.por_entregar ? "blue" : "green"}>{l.codigo_llave}</Badge>
                    ) : l.no_disponible ? (
                      <Badge tone="red">No disponible</Badge>
                    ) : (
                      <input style={{ width: 120 }} value={cod[l.linea_id] || ""}
                             onChange={(e) => setCod({ ...cod, [l.linea_id]: e.target.value })} />
                    )}
                  </td>
                  <td>
                    {l.por_entregar === 1 && (
                      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <button className="btn btn-primary" onClick={() => entregarLinea(l)}>Entregar</button>
                        <Badge tone="blue">Por entregar</Badge>
                      </span>
                    )}
                    {!l.preparada && !l.no_disponible && (
                      <span style={{ display: "flex", gap: 6 }}>
                        <span className="seg-buttons">
                          <button type="button" className={soloPreparar[l.linea_id] ? "on" : ""}
                                  title="Activado: al pulsar OK la llave queda preparada (sin entregar), no prestada"
                                  onClick={() => setSoloPreparar({ ...soloPreparar, [l.linea_id]: !soloPreparar[l.linea_id] })}>
                            Preparar
                          </button>
                        </span>
                        <button className="btn btn-success" onClick={() => preparar(l)}>OK</button>
                        <button className="btn btn-danger" onClick={() => noDisp(l)}>No hay</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {detalle.ruta.estado === "preparado" && (
            <button className="btn btn-primary" style={{ marginTop: 12 }}
                    onClick={() => entregar(detalle.ruta.id)}>Marcar como entregada al solicitante</button>
          )}
        </div>
      )}
      {editar && (
        <EditarRutaAdminModal
          ruta={editar}
          onClose={() => setEditar(null)}
          onGuardado={() => {
            const id = editar.id;
            setEditar(null); cargar(); avisarCambioResumen();
            if (detalle?.ruta.id === id) abrir(id);
          }}
        />
      )}
      {ficha && (
        <FichaLlaveModal
          mensaje={ficha.mensaje}
          defaults={ficha.defaults}
          peligroso={ficha.peligroso}
          onCancel={() => setFicha(null)}
          onConfirmar={(campos) => preparar(ficha.linea, campos)}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------- EDITAR RUTA (admin llaves)
// El admin puede cambiar los pisos PENDIENTES de una solicitud todavía por
// preparar (esté o no empezada). Las líneas ya resueltas (preparadas o "no
// hay") se muestran bloqueadas y se conservan tal cual. La solicitud sigue a
// nombre de su titular.
function EditarRutaAdminModal({ ruta, onClose, onGuardado }) {
  const [cargando, setCargando] = useState(true);
  const [resueltas, setResueltas] = useState([]);
  const [quitar, setQuitar] = useState(() => new Set()); // linea_id de resueltas a quitar
  const { confirmar } = useDialog();
  const toggleQuitar = (id) => setQuitar((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const [texto, setTexto] = useState("");
  const [devol, setDevol] = useState("");
  const [tipoPrestamo, setTipoPrestamo] = useState("provisional");
  const [previa, setPrevia] = useState([]);
  const [msg, setMsg] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api.get(`/rutas/detalle/${ruta.id}`).then(({ data }) => {
      const lineas = data.lineas || [];
      const pend = lineas.filter((l) => !l.preparada && !l.no_disponible);
      setResueltas(lineas.filter((l) => l.preparada || l.no_disponible));
      setTexto(pend.map((l) => `${l.pid} ${l.piso_codigo || ""}`.trim()).join("\n"));
      const ref = pend[0] || lineas[0];
      setDevol(ref?.fecha_devol ? String(ref.fecha_devol).slice(0, 10) : "");
      if (ref?.tipo === "fija") setTipoPrestamo("fija");
    }).catch(() => setMsg({ tipo: "error", texto: "No se pudo cargar la solicitud." }))
      .finally(() => setCargando(false));
  }, [ruta.id]);

  const previsualizar = async () => {
    const { data } = await api.post("/rutas/previsualizar", { texto });
    const filas = data.filas || [];
    setPrevia(filas);
    return filas;
  };

  const guardar = async () => {
    setMsg(null);
    const hayTexto = !!texto.trim();
    const quedanResueltas = resueltas.filter((l) => !quitar.has(l.linea_id)).length;
    if (!hayTexto && !quedanResueltas) {
      setMsg({ tipo: "error", texto: "La solicitud se quedaría sin pisos. Si ya no hace falta, elimínala." }); return;
    }
    if (hayTexto && !devol) { setMsg({ tipo: "error", texto: "La devolución estimada es obligatoria." }); return; }
    if (hayTexto && devol < hoyIso()) { setMsg({ tipo: "error", texto: "La devolución estimada no puede ser anterior a hoy." }); return; }
    setGuardando(true);
    let filas = [];
    if (hayTexto) {
      try {
        filas = await previsualizar();
      } catch {
        setMsg({ tipo: "error", texto: "No se pudo comprobar los pisos." }); setGuardando(false); return;
      }
      const noEncontrados = filas.filter((f) => !f.existe);
      if (noEncontrados.length) {
        const lista = noEncontrados.map((f) => f.id_origen_txt || "(vacío)").join(", ");
        setMsg({ tipo: "error", texto: `No se encuentran estos pisos: ${lista}. Corrígelos (mira las líneas en rojo abajo).` });
        setGuardando(false); return;
      }
    }
    const lineas = filas.map((f) => ({ id_origen_txt: f.id_origen_txt, codigo_piso: f.codigo_piso, fecha_devol: devol, tipo_prestamo: tipoPrestamo }));
    // Quitar líneas con llave ya preparada la deja libre: se confirma.
    const conLlave = resueltas.filter((l) => quitar.has(l.linea_id) && l.preparada);
    if (conLlave.length) {
      const lista = conLlave.map((l) => `· ${l.pid}-${l.codigo_llave ?? "?"} (${l.piso_codigo || "—"})`).join("\n");
      if (!(await confirmar(`Vas a quitar ${conLlave.length} línea(s) con la llave ya preparada:\n${lista}\n\nEsas llaves quedarán libres en el cajetín. ¿Continuar?`))) {
        setGuardando(false); return;
      }
    }
    try {
      const { data } = await api.post("/rutas/admin_editar", { ruta_id: ruta.id, lineas, quitar_lineas: [...quitar] });
      if (data.ok) onGuardado();
      else setMsg({ tipo: "error", texto: data.error || "No se pudo guardar." });
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "Error al guardar los cambios." });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>✏️ Editar solicitud nº {ruta.id} — {ruta.solicitante}</h3>
        {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}
        {cargando ? <p className="muted">Cargando…</p> : (
          <>
            {resueltas.length > 0 && (
              <>
                <label>Ya resueltas <span className="muted" style={{ fontWeight: 400 }}>— se conservan; pulsa ✕ para quitar una de la ruta</span></label>
                <div className="tablewrap">
                  <table>
                    <thead><tr><th>ID piso</th><th>Piso</th><th>Código llave</th><th>Estado</th><th></th></tr></thead>
                    <tbody>
                      {resueltas.map((l) => (
                        <tr key={l.linea_id} className={quitar.has(l.linea_id) ? "fila-quitar" : claseFilaLinea(l)}>
                          <td className="mono">{l.pid ?? "—"}</td>
                          <td>{l.piso_codigo || "—"}</td>
                          <td className="mono">{l.codigo_llave ?? "—"}</td>
                          <td>
                            {quitar.has(l.linea_id)
                              ? <Badge tone="red">Se quitará</Badge>
                              : <Badge tone={tonoEstado(estadoLinea(l))}>{estadoLinea(l)}</Badge>}
                          </td>
                          <td>
                            <button type="button" className={`btn btn-mini ${quitar.has(l.linea_id) ? "" : "acc-rojo"}`}
                                    onClick={() => toggleQuitar(l.linea_id)}
                                    title={quitar.has(l.linea_id) ? "Mantener esta línea" : (l.preparada ? "Quitar (la llave quedará libre)" : "Quitar esta línea")}>
                              {quitar.has(l.linea_id) ? "↩️" : "✕"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <label>Pisos pendientes (uno por línea: nº de piso y opcionalmente el código)</label>
            <textarea rows={8} value={texto} onChange={(e) => setTexto(e.target.value)} />
            <div className="row">
              <div>
                <label>Devolución estimada *</label>
                <input type="date" value={devol} min={hoyIso()} onChange={(e) => setDevol(e.target.value)} />
              </div>
              <div>
                <label>Tipo</label>
                <div className="seg-buttons">
                  <button type="button" className={tipoPrestamo === "provisional" ? "on" : ""} onClick={() => setTipoPrestamo("provisional")}>Provisional</button>
                  <button type="button" className={tipoPrestamo === "fija" ? "on" : ""} onClick={() => setTipoPrestamo("fija")}>Fija</button>
                </div>
              </div>
            </div>
            {previa.length > 0 && (
              <div className="tablewrap" style={{ marginTop: 12 }}>
                <table>
                  <thead><tr><th>Nº</th><th>Código</th><th>Estado</th></tr></thead>
                  <tbody>
                    {previa.map((f, i) => (
                      <tr key={i} className={f.existe ? "fila-ok" : "fila-error"}>
                        <td>{f.id_origen_txt || "—"}</td>
                        <td>{f.codigo_real || f.codigo_piso || "—"}</td>
                        <td>{f.existe ? "✓ encontrado" : "✗ no encontrado"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn" onClick={previsualizar} disabled={cargando || !texto.trim()}>Previsualizar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={cargando || guardando}>
            {guardando ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------- A ENTREGAR / ENTREGADAS (llaves)
// Junto con Pendientes (estado='pedido') cubren el 100% de las rutas: 'A
// entregar' = estado='preparado' (llaves ya listas, falta la entrega física
// al solicitante) y 'Entregadas' = estado='entregado' (cerradas). Se marca
// "entregada" aquí mismo, con un botón directo en la fila — no hace falta
// pasar por el panel principal (Gestionar): los préstamos de la ruta ya
// están en estado 'prestada' desde que se preparan (ver preparar_llave), así
// que ya aparecen correctamente en el Ledger de Gestionar sin nada más que
// hacer; "entregar" aquí es solo cerrar el papeleo de la propia ruta.
function ListaPreparadas({ estado, titulo, conBotonEntregar, onCount }) {
  const [rows, setRows] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [editar, setEditar] = useState(null); // ruta (fila) en edición — solo "A entregar"
  const { avisar, confirmarPeligro, exito } = useDialog();

  // "A entregar" todavía se puede cambiar o eliminar (aún no se ha entregado).
  const eliminar = async (r) => {
    const ok = await confirmarPeligro(
      `Vas a ELIMINAR la solicitud nº ${r.id} de ${r.solicitante} con ${r.n_llaves || 0} piso(s).` +
      `\n\nSus ${r.n_listas || 0} llave(s) preparada(s) volverán a quedar disponibles.` +
      "\n\nEsta acción no se puede deshacer.",
      { titulo: "Eliminar solicitud", boton: "Sí, eliminar" },
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/rutas/eliminar/${r.id}`);
      if (data.ok) { if (detalle?.ruta.id === r.id) setDetalle(null); cargar(); avisarCambioResumen(); }
    } catch (err) { avisar(err.response?.data?.error || "No se pudo eliminar la solicitud."); }
  };
  const [descargando, setDescargando] = useState(null);

  const descargar = async (rutaId) => {
    setDescargando(rutaId);
    const error = await descargarPdf(`/rutas/descargar/${rutaId}`, null, `ruta_${rutaId}.pdf`);
    setDescargando(null);
    if (error) avisar(error);
  };

  const cargar = () => api.get("/rutas/preparadas", { params: { estado } }).then(({ data }) => {
    setRows(data.rows || []);
    onCount?.((data.rows || []).length);
  });
  useEffect(() => { cargar(); }, [estado]); // eslint-disable-line react-hooks/exhaustive-deps
  useActualizarGlobal(cargar);

  // "Fecha pedido" siempre está; según el estado se añaden las siguientes
  // fechas del recorrido de la ruta (prestada/entregado/devuelto).
  const fechasExtra = columnasFecha(estado).slice(1);
  const campos = [
    ["id", "Nº", (r) => r.id],
    ["solicitante", "Solicitante", (r) => r.solicitante],
    ["fecha", "Fecha pedido", (r) => isoToEs(r.fecha_pedido)],
    ...fechasExtra.map(([k, label]) => [k, label, (r) => isoToEs(r[k])]),
    ["llaves", "Llaves", (r) => `${r.n_listas || 0}/${r.n_llaves || 0}`],
  ];
  const { filtros, setFiltro, limpiar, filtradas } = useFiltroColumnas(rows, campos);

  const abrir = async (id) => {
    const { data } = await api.get(`/rutas/detalle/${id}`);
    if (data.ok) setDetalle({ ruta: data.ruta, lineas: data.lineas });
  };

  const entregar = async (rutaId) => {
    try {
      const { data } = await api.post(`/rutas/entregar/${rutaId}`);
      if (data.ok) {
        setDetalle(null); cargar(); avisarCambioResumen();
        exito(`Ruta nº ${rutaId} entregada correctamente: todas sus llaves constan como prestadas.`);
      }
    } catch (err) {
      avisar(err.response?.data?.error || "No se pudo marcar como entregada.");
    }
  };

  // Todo llega del servidor (sin tope); se pinta de 100 en 100.
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(filtradas, [rows, filtros]);
  return (
    <div className="card">
      <PanelHead title={titulo} count={filtradas.length} total={rows.length} onLimpiar={limpiar} />
      <div className="tablewrap">
        <table>
          <thead>
            <FilterHead campos={campos} filtros={filtros} setFiltro={setFiltro} extra={<th></th>} />
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.id}>
                {campos.map(([clave, , get]) => <td key={clave}>{get(r) || "—"}</td>)}
                <td style={{ display: "flex", gap: 6 }}>
                  <button className="btn" onClick={() => abrir(r.id)}>Ver</button>
                  <button className="btn" onClick={() => descargar(r.id)} disabled={descargando === r.id} title="Descargar PDF">
                    {descargando === r.id ? "…" : "⬇️ PDF"}
                  </button>
                  {conBotonEntregar && (
                    <>
                      <button className="btn btn-success" onClick={() => entregar(r.id)}>Entregar</button>
                      <button className="btn" onClick={() => setEditar(r)} title="Editar la solicitud (aún no entregada)">✏️</button>
                      <button className="btn btn-danger" onClick={() => eliminar(r)} title="Eliminar la solicitud">🗑️</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {filtradas.length === 0 && <tr><td colSpan={campos.length + 1} className="empty">Sin solicitudes.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />
      {detalle && (
        <div style={{ marginTop: 16 }}>
          <h4>Solicitud nº {detalle.ruta.id}</h4>
          <div className="tablewrap">
            <table>
              <thead><tr><th>ID piso</th><th>Piso</th><th>Cajetín</th><th>Código llave</th><th>Estado</th></tr></thead>
              <tbody>
                {detalle.lineas.map((l, i) => (
                  <tr key={i} className={claseFilaLinea(l)}>
                    <td className="mono">{l.pid ?? "—"}</td>
                    <td>{l.piso_codigo || "—"}</td>
                    <td>{l.cajetin ?? "—"}</td>
                    <td className="mono">{l.codigo_llave ?? "—"}</td>
                    <td><Badge tone={tonoEstado(estadoLinea(l))}>{estadoLinea(l)}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {conBotonEntregar && detalle.ruta.estado === "preparado" && (
            <button className="btn btn-primary" style={{ marginTop: 12 }}
                    onClick={() => entregar(detalle.ruta.id)}>Marcar como entregada al solicitante</button>
          )}
        </div>
      )}
      {editar && (
        <EditarRutaAdminModal
          ruta={editar}
          onClose={() => setEditar(null)}
          onGuardado={() => {
            const id = editar.id;
            setEditar(null); cargar(); avisarCambioResumen();
            if (detalle?.ruta.id === id) abrir(id);
          }}
        />
      )}
    </div>
  );
}

function AEntregar({ onCount }) {
  return <ListaPreparadas estado="preparado" titulo="Solicitudes a entregar" conBotonEntregar onCount={onCount} />;
}

function Entregadas() {
  return <ListaPreparadas estado="entregado" titulo="Solicitudes entregadas" />;
}

// ------------------------------------------------------- DESCARGA MASIVA (llaves)
// Un solo PDF con todas las rutas preparadas y/o entregadas en un día (por
// defecto hoy) o en un rango de días, opcionalmente acotado por horas (hora
// de España). Cada estado se filtra por su propia fecha: preparadas por la
// fecha en que se prepararon, entregadas por la fecha de entrega.
function DescargaMasivaModal({ onClose }) {
  const [estados, setEstados] = useState({ preparado: true, entregado: true });
  const [desde, setDesde] = useState(hoyEs());
  const [conRango, setConRango] = useState(false);
  const [hasta, setHasta] = useState(hoyEs());
  const [conHoras, setConHoras] = useState(false);
  const [horaDesde, setHoraDesde] = useState("00:00");
  const [horaHasta, setHoraHasta] = useState("23:59");
  const [error, setError] = useState(null);
  const [descargando, setDescargando] = useState(false);

  const toggleEstado = (e) => setEstados((prev) => ({ ...prev, [e]: !prev[e] }));

  const descargar = async () => {
    setError(null);
    const sel = Object.keys(estados).filter((e) => estados[e]);
    if (!sel.length) { setError("Selecciona al menos un estado."); return; }
    if (!desde) { setError("Selecciona una fecha."); return; }
    const fin = conRango ? hasta : desde;
    if (!fin) { setError("Selecciona la fecha final."); return; }
    if (`${fin} ${conHoras ? horaHasta : "23:59"}` < `${desde} ${conHoras ? horaDesde : "00:00"}`) {
      setError("El inicio no puede ser posterior al final."); return;
    }
    const params = { estados: sel.join(","), desde };
    if (conRango) params.hasta = hasta;
    if (conHoras) { params.hora_desde = horaDesde; params.hora_hasta = horaHasta; }
    setDescargando(true);
    const err = await descargarPdf("/rutas/descargar_masivo", params, "rutas.pdf");
    setDescargando(false);
    if (err) setError(err); else onClose();
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>⬇️ Descarga masiva de rutas</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <label>Estado</label>
        <div className="seg-buttons">
          <button type="button" className={estados.preparado ? "on" : ""} data-tone={tonoEstado("preparado")}
                  onClick={() => toggleEstado("preparado")}>Preparadas</button>
          <button type="button" className={estados.entregado ? "on" : ""} data-tone={tonoEstado("entregado")}
                  onClick={() => toggleEstado("entregado")}>Entregadas</button>
        </div>
        <div className="row">
          <div>
            <label>{conRango ? "Desde" : "Fecha"}</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          {conRango && (
            <div>
              <label>Hasta</label>
              <input type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} />
            </div>
          )}
        </div>
        {conHoras && (
          <div className="row">
            <div>
              <label>Hora desde (España)</label>
              <input type="time" value={horaDesde} onChange={(e) => setHoraDesde(e.target.value)} />
            </div>
            <div>
              <label>Hora hasta (España)</label>
              <input type="time" value={horaHasta} onChange={(e) => setHoraHasta(e.target.value)} />
            </div>
          </div>
        )}
        <div className="seg-buttons" style={{ marginTop: 12 }}>
          <button type="button" className={conRango ? "on" : ""} onClick={() => setConRango((v) => !v)}>📅 Seleccionar rango</button>
          <button type="button" className={conHoras ? "on" : ""} onClick={() => setConHoras((v) => !v)}>🕒 Seleccionar horas</button>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={descargar} disabled={descargando}>
            {descargando ? "Descargando…" : "Descargar PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
